import axios from "axios";

// API Configuration
// In production, the Vite build will replace import.meta.env.VITE_APP_API_URL with the actual value
// In development, it will use the value from .env file
const API_BASE_URL = import.meta.env.VITE_APP_API_URL ||
                     process.env.VITE_APP_API_URL;

// Create axios instance without default headers
const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: false, // Don't send cookies
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// Add request interceptor to handle auth headers
api.interceptors.request.use(
  (config) => {
    try {
      // Get auth token
      const token = sessionStorage.getItem("auth_token");

      if (token) {
        // Ensure headers object exists
        if (!config.headers) config.headers = {};

        // Set Authorization header
        config.headers["Authorization"] = `ClientBearer ${token}`;
        config.headers["Content-Type"] = "application/json";
      }
    } catch (error) {
      console.error("Error in request interceptor:", error);
    }

    // Add request deduplication key to prevent duplicate requests
    if (config.method === 'get' || config.method === 'GET') {
      config.params = config.params || {};
      config.params._t = Date.now(); // Add timestamp to prevent caching issues
    }

    return config;
  },
  (error) => {
    console.error("Request interceptor error:", error);
    return Promise.reject(error);
  }
);

// Helper functions for auth data management
export const setAuthData = (authToken, profile) => {
  if (authToken) {
    sessionStorage.setItem("auth_token", authToken);
  }

  if (profile) {
    sessionStorage.setItem("user_profile", JSON.stringify(profile));
  }
};

export const clearAuthData = () => {
  sessionStorage.removeItem("auth_token");
  sessionStorage.removeItem("user_profile");
};

export const getAuthToken = () => {
  return sessionStorage.getItem("auth_token");
};

export const getCurrentUser = () => {
  try {
    const profileJson = sessionStorage.getItem("user_profile");
    if (!profileJson) return null;
    return JSON.parse(profileJson);
  } catch (error) {
    console.error("Error in getCurrentUser:", error);
    return null;
  }
};

// Check user has valid role and status
export const validateUserAccess = () => {
  const profile = getCurrentUser();
  if (!profile) return false;

  // Allow access if user is not declined (including new status)
  return profile.status !== "declined";
};

// Handle auth errors globally
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const errorCode = error.response?.data?.code;
    const errorMessage = error.response?.data?.message;

    // Let components handle these specific cases
    if (errorCode === "INACTIVE_ACCOUNT") {
      return Promise.reject(error);
    }

    if (errorCode === "INVALID_REQUEST" && errorMessage?.includes("reset your password")) {
      return Promise.reject(error);
    }

    // 401 means the session token is invalid or expired — clear and redirect
    if (status === 401) {
      clearAuthData();
      window.location.href = "/sign-in";
      return Promise.reject(error);
    }

    // 403 means authenticated but not authorised for this action — let the component handle it
    return Promise.reject(error);
  }
);

// Password validation regex
const hasUpperCase = /[A-Z]/;
const hasLowerCase = /[a-z]/;
const hasNumber = /\d/;
const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>/-]/;

/**
 * Validate password strength
 * @param {string} password
 * @returns {{
 *   isValid: boolean,
 *   score: number,
 *   feedback: string[],
 *   hasUpperCase: boolean,
 *   hasLowerCase: boolean,
 *   hasNumber: boolean,
 *   hasSpecialChar: boolean,
 *   isLongEnough: boolean
 * }}
 */
export const validatePassword = (password = "") => {
  if (!password) {
    return {
      isValid: false,
      score: 0,
      feedback: ["Password is required"],
      hasUpperCase: false,
      hasLowerCase: false,
      hasNumber: false,
      hasSpecialChar: false,
      isLongEnough: false,
    };
  }

  const requirements = {
    hasUpperCase: hasUpperCase.test(password),
    hasLowerCase: hasLowerCase.test(password),
    hasNumber: hasNumber.test(password),
    hasSpecialChar: hasSpecialChar.test(password),
    isLongEnough: password.length >= 8,
  };

  const feedback = [];
  if (!requirements.hasUpperCase) feedback.push("Add an uppercase letter");
  if (!requirements.hasLowerCase) feedback.push("Add a lowercase letter");
  if (!requirements.hasNumber) feedback.push("Add a number");
  if (!requirements.hasSpecialChar) feedback.push("Add a special character");
  if (!requirements.isLongEnough)
    feedback.push("Make it at least 8 characters");

  const score = Object.values(requirements).filter(Boolean).length;
  const isValid = score === 5;

  return {
    isValid,
    score,
    feedback: isValid ? [] : feedback,
    ...requirements,
  };
};

/**
 * Register a new admin
 * @param {Object} userData
 * @param {string} userData.email
 * @param {string} userData.password
 * @param {string} userData.phone_number
 * @param {string} userData.firstname
 * @param {string} userData.lastname
 * @param {string} userData.role
 * @returns {Promise<{
 *   token_identifier: string,
 *   notification_id: string,
 *   resend_in: number,
 *   role: string
 * }>}
 */
/**
 * Register a new admin user
 * @param {Object} userData
 * @param {string} userData.email
 * @param {string} userData.password
 * @param {string} userData.phone_number
 * @param {string} userData.firstname
 * @param {string} userData.lastname
 * @returns {Promise<{
 *   token_identifier: string,
 *   notification_id: string,
 *   resend_in: number,
 *   role: string
 * }>}
 */
export const registerAdmin = async (userData) => {
  try {
    const response = await api.post(
      `/admin/register`,
      userData,
      {
        timeout: 30000, // 30 seconds timeout
      }
    );

    return response.data;
  } catch (error) {
    let errorMessage = "Registration failed";
    if (error.response) {
      if (error.response.data && error.response.data.message) {
        errorMessage = error.response.data.message;
      } else if (error.response.status === 400) {
        errorMessage = "Invalid request. Please check your input.";
      } else if (error.response.status === 409) {
        errorMessage = "User with this email already exists.";
      } else if (error.response.status >= 500) {
        errorMessage = "Server error. Please try again later.";
      }
    } else if (error.request) {
      errorMessage = "No response from server. Please check your connection.";
    } else {
      errorMessage = `Request error: ${error.message}`;
    }

    const errorWithMessage = new Error(errorMessage);
    errorWithMessage.status = error.response?.status;
    errorWithMessage.code = error.code;
    
    throw errorWithMessage;
  }
};

/**
 * Complete user registration with a one-time token
 * @param {Object} params
 * @param {string} params.token - The one-time token received by the user
 * @param {string} params.password - The new password for the account
 * @returns {Promise<{
 *   id: string,
 *   firstname: string,
 *   lastname: string,
 *   is_activated: boolean,
 *   created: number
 * }>}
 */
export const completeUserRegistration = async ({ token, password }) => {
  try {
    const url = `${API_BASE_URL}/auth-tokens/complete-user-registration/${token}`;

    const response = await axios.put(
      url,
      { new_password: password },
      {
        headers: {
          'Content-Type': 'application/json',
        },
        timeout: 30000, // 30 seconds timeout
      }
    );

    return response.data;
  } catch (error) {
    let errorMessage = "Registration failed";
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      if (error.response.data && error.response.data.message) {
        errorMessage = error.response.data.message;
      } else if (error.response.status === 400) {
        errorMessage = "Invalid request. Please check your input.";
      } else if (error.response.status === 404) {
        errorMessage = "Invalid or expired registration token.";
      } else if (error.response.status >= 500) {
        errorMessage = "Server error. Please try again later.";
      }
    } else if (error.request) {
      // The request was made but no response was received
      errorMessage = "No response from server. Please check your connection.";
    } else {
      // Something happened in setting up the request that triggered an Error
      errorMessage = `Request error: ${error.message}`;
    }

    const errorWithMessage = new Error(errorMessage);
    errorWithMessage.status = error.response?.status;
    errorWithMessage.code = error.code;
    
    throw errorWithMessage;
  }
};

/**
 * Complete 2FA verification
 * @param {string} tokenIdentifier - Token from initial login
 * @param {string} otp - One-time password
 * @returns {Promise<{
 *   auth_token: string,
 *   profile: Object
 * }>}
 */
export const verifyTwoFactor = async (tokenIdentifier, otp) => {
  const response = await api.post(`/auth-tokens/2fa/${tokenIdentifier}`, {
    otp,
  });
  const { auth_token, profile } = response.data;

  // Only block users with declined status
  if (profile.status === "declined") {
    throw new Error(
      "Your account has been declined. Please contact support for more information."
    );
  }

  // Store auth data
  setAuthData(auth_token, profile);
  return { auth_token, profile };
};

/**
 * Login with email/phone and password
 * @param {Object} credentials
 * @param {string} [credentials.email]
 * @param {string} [credentials.phone_number]
 * @param {string} credentials.password
 * @returns {Promise<{
 *   auth_token?: string,
 *   token_identifier?: string,
 *   verification_method?: 'email' | 'sms',
 *   profile?: Object,
 *   inactive_account?: {
 *     notification_id: string,
 *     resend_in: number
 *   }
 * }>}
 */
export const login = async (credentials) => {
  try {
    const response = await api.post("/admin/login", credentials);
    const { auth_token, profile } = response.data;

    // Only block users with declined status
    if (profile && profile.status === "declined") {
      throw new Error(
        "Your account has been declined. Please contact support for more information."
      );
    }

    // Store auth data and return
    setAuthData(auth_token, profile);
    return { auth_token, profile };
  } catch (error) {
    throw error;
  }
};

/**
 * Verify registration with OTP
 * @param {Object} params
 * @param {string} params.token_identifier - Token from registration
 * @param {string} params.otp - One-time password
 * @param {string} params.notification_id - Notification ID
 * @returns {Promise<{
 *   message: string
 * }>}
 */
export const verifyRegistration = async ({
  token_identifier,
  otp,
  notification_id,
}) => {
  // Create a clean axios instance without auth token
  const cleanApi = axios.create({
    baseURL: API_BASE_URL,
    headers: {
      "Content-Type": "application/json",
    },
  });

  const response = await cleanApi.put(
    `/auth-tokens/account-activation/${token_identifier}`,
    {
      otp,
      notification_id,
    }
  );
  return response.data;
};

/**
 * Logout the current user
 */
export const logout = async () => {
  try {
    // Get the token before clearing auth data
    const token = getAuthToken();

    if (!token) {
      clearAuthData();
      return;
    }

    // Create a clean axios instance for logout
    const cleanApi = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        Authorization: `ClientBearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    // Make the logout request and wait for it to complete
    // A 204 response is expected and is a success
    await cleanApi.post("/admin/logout");

    // Clear auth data after successful API call
    clearAuthData();
  } catch (error) {
    // Check if it's a 204 response (no content) which is actually success
    if (error.response && error.response.status === 204) {
      clearAuthData();
      return;
    }

    // Still clear auth data on error to ensure user is logged out locally
    clearAuthData();
    throw error; // Re-throw to allow proper error handling upstream
  }
};

/**
 * Request password reset
 * @param {string} email
 * @returns {Promise<{ email: string, notification_id: string, resend_in: number }>}
 */
export const requestPasswordReset = async (email) => {
  const response = await api.post("/admin/forgotten-password", {
    email,
  });
  return response.data;
};

/**
 * Reset password with token (user-initiated)
 * @param {string} token - Token from email for forgotten password
 * @param {string} newPassword
 * @returns {Promise<void>}
 */
export const resetForgottenPassword = async (token, newPassword) => {
  await api.put(`/auth-tokens/forgotten-password/${token}`, {
    new_password: newPassword,
  });
};

/**
 * Reset password with token (system-initiated)
 * @param {string} resetToken - Reset token from email for system reset
 * @param {string} newPassword
 * @returns {Promise<void>}
 */
export const resetSystemPassword = async (resetToken, newPassword) => {
  await api.put(`/auth-tokens/password-reset/${resetToken}`, {
    new_password: newPassword,
  });
};

/**
 * Helper to check if user is authenticated
 * @param {boolean} strictCheck - If true, also validates user access (approved status)
 * @returns {boolean}
 */
export const isAuthenticated = (strictCheck = true) => {
  const hasToken = !!getAuthToken();

  // If no token, definitely not authenticated
  if (!hasToken) return false;

  // If not doing strict check, just check if token exists
  if (!strictCheck) return true;

  // If doing strict check, also validate user access
  return validateUserAccess();
};

/**
 * Resend notification OTP
 * @param {string} notification_id - The ID of the notification to resend
 * @returns {Promise<{
 *   message: string,
 *   resend_in?: number
 * }>}
 */
export const resendNotification = async (notification_id) => {
  const response = await api.post("/admin/notifications/resend", {
    notification_id,
  });
  return response.data;
};

/**
 * Verify OTP for account activation
 * @param {Object} params
 * @param {string} params.token_identifier - Token identifier
 * @param {string} params.otp - One-time password
 * @param {string} params.notification_id - Notification ID
 * @returns {Promise<{
 *   message: string
 * }>}
 */
export const verifyOTP = async ({ token_identifier, otp, notification_id }) => {
  const response = await api.put(
    `/auth-tokens/account-activation/${token_identifier}`,
    {
      otp,
      notification_id,
    }
  );
  return response.data;
};

/**
 * Complete 2FA verification process
 * @param {Object} params
 * @param {string} params.token_identifier - Token from initial login
 * @param {string} params.otp - One-time password
 * @returns {Promise<{
 *   auth_token: string,
 *   profile: Object
 * }>}
 */
export const completeTwoFactorLogin = async ({ token_identifier, otp }) => {
  const response = await api.post(`/auth-tokens/2fa/${token_identifier}`, {
    otp,
  });

  const { auth_token, profile } = response.data;

  // Store auth data
  setAuthData(auth_token, profile);
  return response.data;
};

// Export the API instance for use in other services
export { api };
